import { useState, useEffect, useMemo } from "react";
import API from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MagnifyingGlass, CheckCircle, Storefront } from "@phosphor-icons/react";
import { toast } from "sonner";

const COUNTRIES = [
  { code: "US", name: "United States" },
  { code: "GB", name: "United Kingdom" },
  { code: "CA", name: "Canada" },
  { code: "AU", name: "Australia" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "IN", name: "India" },
  { code: "JP", name: "Japan" },
];

export default function SkimlinksImportDialog({ open, onOpenChange, onImported }) {
  const [verticals, setVerticals] = useState([]);
  const [verticalId, setVerticalId] = useState("");
  const [country, setCountry] = useState("US");
  const [minRate, setMinRate] = useState(3);
  const [merchants, setMerchants] = useState(null); // null = not loaded, [] = empty
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [hideImported, setHideImported] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    setQuery("");
    setMerchants(null);
    (async () => {
      try {
        const { data } = await API.get("/admin/skimlinks/verticals");
        setVerticals(data.data || []);
        if (!verticalId && data.data?.length) setVerticalId(String(data.data[0].id));
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Failed to load Skimlinks verticals");
      }
    })();
  }, [open, verticalId]);

  const selectedVertical = verticals.find(v => String(v.id) === String(verticalId));

  const loadMerchants = async () => {
    if (!verticalId) {
      toast.error("Pick a vertical first");
      return;
    }
    setLoading(true);
    setSelected(new Set());
    try {
      const { data } = await API.get("/admin/skimlinks/merchants", {
        params: {
          vertical_id: verticalId,
          taxonomy: selectedVertical?.taxonomy || "top_merchant",
          country,
          min_rate: minRate,
        },
      });
      setMerchants(data.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load merchants");
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const list = merchants || [];
    const q = query.trim().toLowerCase();
    return list.filter((m) => {
      if (hideImported && m.already_imported) return false;
      if (!q) return true;
      return (
        m.name?.toLowerCase().includes(q) ||
        m.campaign_id?.toLowerCase().includes(q) ||
        m.domain?.toLowerCase().includes(q)
      );
    });
  }, [merchants, query, hideImported]);

  const toggle = (cid) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(cid)) next.delete(cid); else next.add(cid);
      return next;
    });
  };

  const toggleAllVisible = () => {
    const visibleUnimported = filtered.filter((m) => !m.already_imported).map((m) => m.campaign_id);
    const allSelected = visibleUnimported.every((id) => selected.has(id));
    setSelected(prev => {
      const next = new Set(prev);
      if (allSelected) visibleUnimported.forEach((id) => next.delete(id));
      else visibleUnimported.forEach((id) => next.add(id));
      return next;
    });
  };

  const runImport = async () => {
    if (selected.size === 0) {
      toast.error("Select at least one brand");
      return;
    }
    setImporting(true);
    try {
      const { data } = await API.post("/admin/skimlinks/brands/import", {
        campaign_ids: Array.from(selected),
        vertical_id: Number(verticalId),
        taxonomy: selectedVertical?.taxonomy || "top_merchant",
        country,
      });
      const rr = data.rate_refresh || {};
      const refreshed = (rr.updated || 0) + (rr.unchanged || 0);
      toast.success(
        `Imported ${data.imported} brand${data.imported === 1 ? "" : "s"}` +
          (refreshed ? ` · rates synced for ${refreshed}` : "") +
          (rr.missing ? ` · ${rr.missing} missing from global feed` : "")
      );
      if (onImported) onImported();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const totalImported = (merchants || []).filter((m) => m.already_imported).length;
  const selectedCount = selected.size;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0 gap-0 overflow-hidden" data-testid="skimlinks-import-dialog">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-200">
          <DialogTitle className="font-heading text-xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <Storefront size={20} weight="duotone" className="text-orange-500" />
            Import brands from Skimlinks
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            Filter by vertical + country, then pick merchants with a commission rate above your threshold.
          </DialogDescription>
        </DialogHeader>

        {/* Filter row */}
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/30 grid grid-cols-[1.4fr_1fr_0.8fr_auto] gap-3 items-end">
          <div>
            <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400 block mb-1">Vertical</label>
            <Select value={verticalId} onValueChange={setVerticalId}>
              <SelectTrigger className="h-9 text-sm" data-testid="skimlinks-vertical-select"><SelectValue placeholder="Pick a vertical" /></SelectTrigger>
              <SelectContent>
                {verticals.map(v => (
                  <SelectItem key={v.id} value={String(v.id)}>
                    {v.name} <span className="text-slate-400">({v.taxonomy})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400 block mb-1">Country</label>
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger className="h-9 text-sm" data-testid="skimlinks-country-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {COUNTRIES.map(c => (
                  <SelectItem key={c.code} value={c.code}>{c.name} ({c.code})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400 block mb-1">Min rate %</label>
            <Input
              type="number"
              step="0.5"
              min="0"
              value={minRate}
              onChange={(e) => setMinRate(Number(e.target.value) || 0)}
              className="h-9 text-sm"
              data-testid="skimlinks-minrate-input"
            />
          </div>
          <Button
            onClick={loadMerchants}
            disabled={loading || !verticalId}
            className="h-9 bg-orange-500 hover:bg-orange-600 text-white"
            data-testid="skimlinks-load-btn"
          >
            {loading ? "Loading…" : "Load merchants"}
          </Button>
        </div>

        {/* Results area */}
        {merchants === null ? (
          <div className="px-6 py-24 text-center text-sm text-slate-400">
            Pick a vertical and click <span className="font-semibold text-slate-600">Load merchants</span> to fetch.
          </div>
        ) : (
          <>
            <div className="px-6 py-3 border-b border-slate-100 flex items-center gap-3">
              <div className="relative flex-1">
                <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder={`Search within ${merchants.length} merchants…`}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="pl-8 h-9 text-sm"
                  data-testid="skimlinks-search-input"
                  autoFocus
                />
              </div>
              <label className="flex items-center gap-2 cursor-pointer text-slate-600 text-xs whitespace-nowrap">
                <Checkbox checked={hideImported} onCheckedChange={(v) => setHideImported(!!v)} />
                Hide imported
              </label>
              <button className="text-[#002FA7] text-xs font-medium hover:underline whitespace-nowrap" onClick={toggleAllVisible}>
                {filtered.every((m) => m.already_imported || selected.has(m.campaign_id)) ? "Clear visible" : "Select all visible"}
              </button>
            </div>
            <ScrollArea className="max-h-[400px]">
              <div className="divide-y divide-slate-100">
                {filtered.length === 0 ? (
                  <div className="px-6 py-16 text-center text-sm text-slate-400">
                    {merchants.length === 0 ? `No merchants ≥ ${minRate}% in this vertical · country.` : "No matches — tweak search or filter."}
                  </div>
                ) : filtered.map((m) => {
                  const isSel = selected.has(m.campaign_id);
                  const disabled = m.already_imported;
                  return (
                    <div
                      key={m.campaign_id}
                      className={`flex items-start gap-3 px-6 py-3 transition-colors ${
                        disabled ? "bg-slate-50/50" : isSel ? "bg-orange-50/60" : "hover:bg-slate-50 cursor-pointer"
                      }`}
                      onClick={() => !disabled && toggle(m.campaign_id)}
                      data-testid={`skimlinks-row-${m.campaign_id}`}
                    >
                      <div className="pt-0.5">
                        {disabled ? (
                          <CheckCircle size={18} weight="fill" className="text-emerald-500" />
                        ) : (
                          <Checkbox checked={isSel} onCheckedChange={() => toggle(m.campaign_id)} onClick={(e) => e.stopPropagation()} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="font-semibold text-slate-900 text-sm">{m.name || "—"}</span>
                          {disabled && (
                            <span className="text-[10px] font-bold tracking-wider uppercase text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
                              Imported
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5 truncate">{m.domain || "—"}</div>
                        <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-400 font-mono">
                          <span>ID: {m.campaign_id}</span>
                          {m.countries?.length > 0 && <span>{m.countries.slice(0, 3).join(" · ")}</span>}
                        </div>
                      </div>
                      <div className="text-right whitespace-nowrap">
                        <div className="font-mono text-sm font-bold text-orange-600">{m.best_rate.toFixed(1)}%</div>
                        <div className="text-[10px] text-slate-400 mt-0.5">{m.best_rate_description}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
            <div className="px-6 py-2 border-t border-slate-100 bg-slate-50/40 text-[11px] text-slate-500 flex justify-between">
              <span>{merchants.length} total matching · {totalImported} already imported</span>
              <span>Sorted by commission ↓</span>
            </div>
          </>
        )}

        <DialogFooter className="px-6 py-4 border-t border-slate-200 bg-slate-50/50 flex items-center justify-between gap-3">
          <span className="text-xs text-slate-500" data-testid="skimlinks-selected-count">{selectedCount} selected</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={importing}>Cancel</Button>
            <Button
              size="sm"
              onClick={runImport}
              disabled={importing || selectedCount === 0}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="skimlinks-import-btn"
            >
              {importing ? "Importing…" : `Import ${selectedCount || ""} brand${selectedCount === 1 ? "" : "s"}`}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
